import React, { createContext, useContext, useMemo } from 'react';

// Types for utilities
export interface Utility {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: string;
  code: string;
  language: string;
}

export interface UtilitiesContextType {
  utilities: Utility[];
  getUtilityByCategory: (category: string) => Utility[];
  getUtilityById: (id: string) => Utility | undefined;
  getUtilityCodeSnippet: (id: string) => string | null;
  getAllCategories: () => string[];
  searchUtilities: (query: string) => Utility[];
  getUtilitiesMetadata: () => {
    totalCount: number;
    categoryCounts: Record<string, number>;
    availableLanguages: string[];
  };
}

// Create the context
const UtilitiesContext = createContext<UtilitiesContextType | undefined>(undefined);

// Utility data - same as in Utilities.tsx but exported for context
export const utilitiesData: Utility[] = [
  {
    id: '1',
    title: 'Password Generator',
    description: 'Generate secure passwords with customizable options',
    category: 'Security',
    icon: 'security',
    code: `// Secure Password Generator Utility
export class PasswordGenerator {
  static defaultOptions = {
    length: 12,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeSimilar: false,
    excludeAmbiguous: false,
  };

  static generate(options = {}) {
    const config = { ...this.defaultOptions, ...options };
    
    let charset = '';
    if (config.includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (config.includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (config.includeNumbers) charset += '0123456789';
    if (config.includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    if (config.excludeSimilar) {
      charset = charset.replace(/[il1Lo0O]/g, '');
    }
    
    if (config.excludeAmbiguous) {
      charset = charset.replace(/[{}\\[\\]()\/\\\\'"~,;<>.]/g, '');
    }
    
    let password = '';
    for (let i = 0; i < config.length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    return password;
  }
  
  static checkStrength(password) {
    let score = 0;
    const checks = [
      { regex: /.{8,}/, points: 1 }, // At least 8 characters
      { regex: /.{12,}/, points: 1 }, // At least 12 characters
      { regex: /[a-z]/, points: 1 }, // Lowercase
      { regex: /[A-Z]/, points: 1 }, // Uppercase
      { regex: /[0-9]/, points: 1 }, // Numbers
      { regex: /[^A-Za-z0-9]/, points: 1 }, // Special characters
      { regex: /.{16,}/, points: 1 }, // Very long
    ];
    
    checks.forEach(check => {
      if (check.regex.test(password)) score += check.points;
    });
    
    if (score < 3) return 'Weak';
    if (score < 5) return 'Medium';
    if (score < 6) return 'Strong';
    return 'Very Strong';
  }
}

// Usage Examples
const password = PasswordGenerator.generate({
  length: 16,
  includeSymbols: true,
  excludeSimilar: true
});

console.log('Generated Password:', password);
console.log('Strength:', PasswordGenerator.checkStrength(password));`,
    language: 'javascript'
  },
  {
    id: '2',
    title: 'JWT Handler',
    description: 'Complete JWT token management utilities',
    category: 'Authentication',
    icon: 'verified-user',
    code: `// JWT Token Handler Utility
import jwt from 'jsonwebtoken';

export class JWTHandler {
  constructor(secretKey = 'your-secret-key') {
    this.secretKey = secretKey;
  }
  
  generateToken(payload, expiresIn = '24h') {
    return jwt.sign(payload, this.secretKey, { expiresIn });
  }
  
  verifyToken(token) {
    try {
      return jwt.verify(token, this.secretKey);
    } catch (error) {
      throw new Error('Invalid token');
    }
  }
  
  decodeToken(token) {
    return jwt.decode(token, { complete: true });
  }
  
  refreshToken(token, newExpiresIn = '24h') {
    const decoded = this.verifyToken(token);
    delete decoded.iat;
    delete decoded.exp;
    
    return this.generateToken(decoded, newExpiresIn);
  }
}

// Usage Example
const jwtHandler = new JWTHandler('your-secret-key');
const token = jwtHandler.generateToken({ userId: 123, role: 'admin' });
console.log('Generated Token:', token);

const verified = jwtHandler.verifyToken(token);
console.log('Verified Payload:', verified);`,
    language: 'javascript'
  },
  {
    id: '3',
    title: 'Data Validator',
    description: 'Validate various data formats and types',
    category: 'Validation',
    icon: 'check-circle',
    code: `// Data Validator Utility
export class DataValidator {
  static isEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  static isPhoneNumber(phone) {
    const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone);
  }
  
  static isURL(url) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }
  
  static isStrongPassword(password) {
    // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return strongPasswordRegex.test(password);
  }
  
  static validateObject(obj, schema) {
    const errors = [];
    
    for (const [key, rules] of Object.entries(schema)) {
      const value = obj[key];
      
      if (rules.required && (!value || value === '')) {
        errors.push(\`\${key} is required\`);
        continue;
      }
      
      if (value && rules.type && typeof value !== rules.type) {
        errors.push(\`\${key} must be of type \${rules.type}\`);
      }
      
      if (value && rules.minLength && value.length < rules.minLength) {
        errors.push(\`\${key} must be at least \${rules.minLength} characters\`);
      }
      
      if (value && rules.maxLength && value.length > rules.maxLength) {
        errors.push(\`\${key} must be no more than \${rules.maxLength} characters\`);
      }
      
      if (value && rules.pattern && !rules.pattern.test(value)) {
        errors.push(\`\${key} format is invalid\`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  static sanitizeInput(input, options = {}) {
    if (typeof input !== 'string') return input;
    
    let sanitized = input;
    
    if (options.trim !== false) {
      sanitized = sanitized.trim();
    }
    
    if (options.removeHtml) {
      sanitized = sanitized.replace(/<[^>]*>/g, '');
    }
    
    if (options.escapeHtml) {
      sanitized = sanitized
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
    
    if (options.removeSpecialChars) {
      sanitized = sanitized.replace(/[^a-zA-Z0-9\s]/g, '');
    }
    
    return sanitized;
  }
}

// Usage Examples
console.log('Email valid:', DataValidator.isEmail('test@example.com'));
console.log('Phone valid:', DataValidator.isPhoneNumber('+1-234-567-8900'));
console.log('URL valid:', DataValidator.isURL('https://example.com'));

const userSchema = {
  name: { required: true, type: 'string', minLength: 2 },
  email: { required: true, type: 'string', pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
  age: { required: false, type: 'number' }
};

const userData = { name: 'John', email: 'john@example.com', age: 25 };
const validation = DataValidator.validateObject(userData, userSchema);
console.log('Validation result:', validation);`,
    language: 'javascript'
  },
  {
    id: '4',
    title: 'File Compression',
    description: 'Compress and decompress files efficiently',
    category: 'File System',
    icon: 'archive',
    code: `// File Compression Utility
import pako from 'pako';

export class FileCompression {
  static compressString(input) {
    try {
      const compressed = pako.gzip(input);
      return {
        compressed: Array.from(compressed),
        originalSize: input.length,
        compressedSize: compressed.length,
        compressionRatio: ((1 - compressed.length / input.length) * 100).toFixed(2) + '%'
      };
    } catch (error) {
      throw new Error('Compression failed: ' + error.message);
    }
  }
  
  static decompressString(compressedArray) {
    try {
      const compressed = new Uint8Array(compressedArray);
      const decompressed = pako.ungzip(compressed, { to: 'string' });
      return decompressed;
    } catch (error) {
      throw new Error('Decompression failed: ' + error.message);
    }
  }
  
  static async compressFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = function(event) {
        try {
          const arrayBuffer = event.target.result;
          const uint8Array = new Uint8Array(arrayBuffer);
          const compressed = pako.gzip(uint8Array);
          
          resolve({
            compressed: compressed,
            originalSize: uint8Array.length,
            compressedSize: compressed.length,
            compressionRatio: ((1 - compressed.length / uint8Array.length) * 100).toFixed(2) + '%',
            fileName: file.name + '.gz'
          });
        } catch (error) {
          reject(new Error('File compression failed: ' + error.message));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }
  
  static createDownloadLink(compressed, fileName) {
    const blob = new Blob([compressed], { type: 'application/gzip' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }
  
  static getCompressionStats(original, compressed) {
    return {
      originalSize: original.length,
      compressedSize: compressed.length,
      savedBytes: original.length - compressed.length,
      compressionRatio: ((1 - compressed.length / original.length) * 100).toFixed(2) + '%',
      spaceSaved: (((original.length - compressed.length) / original.length) * 100).toFixed(2) + '%'
    };
  }
}

// Usage Examples
const text = "This is a sample text that will be compressed using gzip compression.";
const result = FileCompression.compressString(text);
console.log('Compression result:', result);

const decompressed = FileCompression.decompressString(result.compressed);
console.log('Decompressed text:', decompressed);`,
    language: 'javascript'
  },
  {
    id: '5',
    title: 'Cache Manager',
    description: 'Advanced caching with TTL and statistics',
    category: 'Performance',
    icon: 'speed',
    code: `// Advanced Cache Manager Utility
export class CacheManager {
  constructor(defaultTTL = 300000) { // 5 minutes default
    this.cache = new Map();
    this.timers = new Map();
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
      evictions: 0
    };
    this.defaultTTL = defaultTTL;
  }
  
  set(key, value, ttl = this.defaultTTL) {
    // Clear existing timer if key exists
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }
    
    // Store the value with metadata
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl,
      accessCount: 0
    });
    
    // Set expiration timer
    if (ttl > 0) {
      const timer = setTimeout(() => {
        this.delete(key);
        this.stats.evictions++;
      }, ttl);
      
      this.timers.set(key, timer);
    }
    
    this.stats.sets++;
    return true;
  }
  
  get(key) {
    const item = this.cache.get(key);
    
    if (!item) {
      this.stats.misses++;
      return null;
    }
    
    // Check if item has expired
    if (item.ttl > 0 && Date.now() - item.timestamp > item.ttl) {
      this.delete(key);
      this.stats.misses++;
      this.stats.evictions++;
      return null;
    }
    
    // Update access statistics
    item.accessCount++;
    this.stats.hits++;
    
    return item.value;
  }
  
  has(key) {
    return this.cache.has(key) && this.get(key) !== null;
  }
  
  delete(key) {
    const existed = this.cache.has(key);
    
    if (existed) {
      this.cache.delete(key);
      
      if (this.timers.has(key)) {
        clearTimeout(this.timers.get(key));
        this.timers.delete(key);
      }
      
      this.stats.deletes++;
    }
    
    return existed;
  }
  
  clear() {
    // Clear all timers
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this.cache.clear();
    
    // Reset stats
    Object.keys(this.stats).forEach(key => this.stats[key] = 0);
  }
  
  size() {
    return this.cache.size;
  }
  
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    return {
      ...this.stats,
      hitRate: total > 0 ? ((this.stats.hits / total) * 100).toFixed(2) + '%' : '0%',
      size: this.size(),
      memoryUsage: this.getMemoryUsage()
    };
  }
  
  getMemoryUsage() {
    let totalSize = 0;
    this.cache.forEach((item, key) => {
      totalSize += this.estimateSize(key) + this.estimateSize(item);
    });
    return this.formatBytes(totalSize);
  }
  
  estimateSize(obj) {
    return JSON.stringify(obj).length * 2; // Rough estimation
  }
  
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  // Advanced: Memoization wrapper
  async memoize(key, asyncFunction, ttl = this.defaultTTL) {
    const cached = this.get(key);
    if (cached !== null) {
      return cached;
    }
    
    const result = await asyncFunction();
    this.set(key, result, ttl);
    return result;
  }
}

// Usage Examples
const cache = new CacheManager();

// Basic usage
cache.set('user:123', { name: 'John', email: 'john@example.com' }, 300); // 5 min TTL
const user = cache.get('user:123');

// Memoization example
async function fetchUserData(userId) {
  // Simulate API call
  return { id: userId, name: 'User ' + userId };
}

const userData = await cache.memoize('user:456', () => fetchUserData(456), 600);
console.log('Cache stats:', cache.getStats());`,
    language: 'javascript'
  }
];

// Provider component
export const UtilitiesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const contextValue = useMemo<UtilitiesContextType>(() => ({
    utilities: utilitiesData,
    
    getUtilityByCategory: (category: string) => {
      if (category === 'All') return utilitiesData;
      return utilitiesData.filter(utility => utility.category === category);
    },
    
    getUtilityById: (id: string) => {
      return utilitiesData.find(utility => utility.id === id);
    },
    
    getUtilityCodeSnippet: (id: string) => {
      const utility = utilitiesData.find(utility => utility.id === id);
      return utility ? utility.code : null;
    },
    
    getAllCategories: () => {
      const categories = ['All', ...new Set(utilitiesData.map(utility => utility.category))];
      return categories;
    },
    
    searchUtilities: (query: string) => {
      const searchTerm = query.toLowerCase();
      return utilitiesData.filter(utility =>
        utility.title.toLowerCase().includes(searchTerm) ||
        utility.description.toLowerCase().includes(searchTerm) ||
        utility.category.toLowerCase().includes(searchTerm) ||
        utility.code.toLowerCase().includes(searchTerm)
      );
    },
    
    getUtilitiesMetadata: () => {
      const categoryCounts: Record<string, number> = {};
      const languages = new Set<string>();
      
      utilitiesData.forEach(utility => {
        categoryCounts[utility.category] = (categoryCounts[utility.category] || 0) + 1;
        languages.add(utility.language);
      });
      
      return {
        totalCount: utilitiesData.length,
        categoryCounts,
        availableLanguages: Array.from(languages),
      };
    },
  }), []);

  return (
    <UtilitiesContext.Provider value={contextValue}>
      {children}
    </UtilitiesContext.Provider>
  );
};

// Hook to use utilities context
export const useUtilities = () => {
  const context = useContext(UtilitiesContext);
  if (context === undefined) {
    throw new Error('useUtilities must be used within a UtilitiesProvider');
  }
  return context;
};

// Export utilities data for direct import
export { utilitiesData as utilities };
